import { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, Marker } from '@react-google-maps/api';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslate } from '@tolgee/react';
import { Search, MapPin, Navigation, ChevronUp, ChevronDown } from 'lucide-react';
import apiManager from '../lib/api';
import { StoreLocation, StoreCategory } from '../lib/api/services/store';

const mapContainerStyle = {
  width: '100%',
  height: '100vh',
};

// Premium dark style para el mapa
const mapOptions = {
  disableDefaultUI: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControl: true,
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
    { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
    { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
    { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
  ],
};

const defaultCenter = { lat: 40.4168, lng: -3.7038 }; // Madrid center

const StoresLocatorPage = () => {
  const { t } = useTranslate();
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [stores, setStores] = useState<StoreLocation[]>([]);
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [center, setCenter] = useState(defaultCenter);
  const [selectedStore, setSelectedStore] = useState<StoreLocation | null>(null);
  const [map, setMap] = useState<any | null>(null);

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    initOnMount: false,
    requestOptions: {
      componentRestrictions: { country: "es" }
    },
    debounce: 300,
  });

const libraries: ("places")[] = ["places"];

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries: libraries
  });

  useEffect(() => {
    if (isLoaded) {
      init();
    }
  }, [isLoaded, init]);

  const fetchCategories = async () => {
    try {
      const response = await apiManager.stores.getCategories();
      if (response.success && response.data) {
        setCategories(response.data.filter((c: any) => c.isActive !== false));
      }
    } catch(err) {
      console.error(err);
    }
  };

  const fetchStores = async (bounds?: { neLat: number, neLng: number, swLat: number, swLng: number }, lat?: number, lng?: number) => {
    let params: any = {};
    if (bounds) {
      params = { ...params, ...bounds };
    } else if (lat && lng) {
      params = { ...params, lat, lng, radius: 50 };
    }
    
    if (selectedCategory) params.category = selectedCategory;

    try {
      const response = await apiManager.stores.getStores(params);
      if (response.success && response.data) {
        setStores(response.data);
      }
    } catch(err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const onMapIdle = useCallback(() => {
    if (map) {
      const bounds = map.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        fetchStores({ neLat: ne.lat(), neLng: ne.lng(), swLat: sw.lat(), swLng: sw.lng() });
      }
    } else {
      // Fallback si el mapa aún no carga
      fetchStores(undefined, center.lat, center.lng);
    }
  }, [map, selectedCategory, center]);

  useEffect(() => {
    onMapIdle();
  }, [selectedCategory, onMapIdle]);

  const onMapLoad = useCallback((mapInstance: any) => {
    setMap(mapInstance);
  }, []);

  const handleSelectPlace = async (address: string) => {
    setValue(address, false);
    clearSuggestions();
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      setCenter({ lat, lng });
      if (map) {
        map.panTo({ lat, lng });
        map.setZoom(12);
      }
    } catch (error) {
      console.log('Error: ', error);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setCenter({ lat, lng });
          if(map){
            map.panTo({ lat, lng });
            map.setZoom(13);
          }
        },
        () => null
      );
    }
  };

  if (loadError) return <div className="p-10 text-center text-red-500">Error al cargar Google Maps. Verifica tu API KEY.</div>;

  return (
    <div className="relative w-full h-[calc(100vh-64px)] bg-[#111111] overflow-hidden text-white font-sans">
      
      {/* Sidebar de búsqueda (Glassmorphism oscuro) */}
      <div className={`absolute bottom-0 left-0 lg:bottom-auto lg:top-0 w-full sm:w-96 md:w-[450px] transition-all duration-500 ease-in-out z-10 pointer-events-none p-0 lg:p-6 flex flex-col ${isMobileExpanded ? 'h-[80vh]' : 'h-auto'} lg:h-full`}>
        <motion.div 
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", damping: 20, stiffness: 100 }}
          className="pointer-events-auto bg-black/60 backdrop-blur-xl border border-x-0 lg:border-x border-white/10 rounded-t-3xl lg:rounded-2xl shadow-[0_-15px_40px_rgba(0,0,0,0.5)] lg:shadow-2xl px-5 pt-6 pb-8 lg:p-6 flex flex-col h-full overflow-hidden"
        >
          <div className="mb-4 md:mb-6 flex justify-between items-start">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-yellow-500 mb-1 leading-tight">
                Nuestros Distribuidores
              </h1>
              <p className="text-xs text-gray-300 hidden md:block">
                Encuentra tu tienda o distribuidor oficial más cercano y descubre toda nuestra gama de productos Jarad.
              </p>
            </div>
            
            <button 
              onClick={() => setIsMobileExpanded(!isMobileExpanded)}
              className="lg:hidden p-2 rounded-full bg-white/10 hover:bg-white/20 text-white pointer-events-auto shadow-sm ml-2 flex-shrink-0 transition-colors"
            >
              {isMobileExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>

          {/* Buscador de lugares */}
          <div className="relative mb-4">
            <div className="relative w-full">
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={!ready}
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl pl-[38px] md:pl-10 pr-[105px] md:pr-28 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 transition-all placeholder-gray-400 text-xs md:text-sm"
                placeholder={t('stores.search_placeholder', "Ciudad, CP...")}
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              
              <button 
                onClick={getUserLocation}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-yellow-400 hover:bg-yellow-500 text-black px-2.5 py-1.5 rounded-lg flex items-center gap-1 font-bold text-[10px] md:text-xs shadow-none transition-transform hover:scale-105"
                title="Usar mi ubicación actual"
              >
                <Navigation size={14} fill="currentColor" />
                Cerca de mí
              </button>
            </div>

            {/* Suggestions dropdown */}
            <AnimatePresence>
              {status === "OK" && (
                <motion.ul 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-20 w-full mt-2 bg-gray-900 border border-white/10 rounded-xl max-h-60 overflow-y-auto shadow-xl"
                >
                  {data.map(({ place_id, description }: any) => (
                    <li 
                      key={place_id} 
                      className="px-4 py-3 hover:bg-white/5 cursor-pointer text-sm text-gray-200 border-b border-white/5 last:border-0"
                      onClick={() => handleSelectPlace(description)}
                    >
                      {description}
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>

          {/* Contenido colapsable (Filtrado y Lista) */}
          <div className={`flex flex-col flex-1 overflow-hidden transition-all duration-500 ease-in-out ${isMobileExpanded ? 'opacity-100 mt-4' : 'hidden lg:flex lg:opacity-100 lg:mt-4'}`}>
            {/* Filtro de Categorías */}
            <div className="mb-6 overflow-x-auto pb-2 scrollbar-hide flex-shrink-0">
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedCategory('')}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === '' 
                    ? 'bg-yellow-400 text-black' 
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                Todas
              </button>
              {categories.map((cat) => (
                <button
                  key={cat._id}
                  onClick={() => setSelectedCategory(cat._id === selectedCategory ? '' : cat._id)}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${
                    selectedCategory === cat._id 
                      ? 'bg-yellow-400 text-black' 
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.pinColor }}></span>
                  <span>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Lista de Resultados */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
            {stores.length === 0 ? (
              <div className="text-center text-gray-400 py-10">
                <MapPin className="mx-auto mb-3 opacity-50" size={32} />
                <p>No se encontraron distribuidores en esta zona.</p>
              </div>
            ) : (
              stores.map((store) => (
                <motion.div 
                  key={store._id}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => {
                    setSelectedStore(store);
                    setCenter({ lat: store.location.coordinates[1], lng: store.location.coordinates[0] });
                    if(map) {
                      map.panTo({ lat: store.location.coordinates[1], lng: store.location.coordinates[0] });
                      map.setZoom(16);
                    }
                  }}
                  className={`p-4 rounded-xl cursor-pointer transition-all border ${
                    selectedStore?._id === store._id 
                      ? 'bg-yellow-400/10 border-yellow-400/50' 
                      : 'bg-white/5 border-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-white mb-1 group-hover:text-yellow-400 transition-colors">{store.name}</h3>
                      {store.category && (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm mb-2 inline-block bg-white/10 text-gray-300" style={{ borderLeft: `2px solid ${store.category.pinColor}`}}>
                          {store.category.name}
                        </span>
                      )}
                      
                      {store.address && (
                        <p className="text-xs text-gray-400 flex items-start mt-1">
                          <MapPin size={12} className="mr-1.5 mt-0.5 flex-shrink-0" />
                          <span>{store.address}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
          </div>
        </motion.div>
      </div>

      {/* El Mapa! */}
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={6} // Zoom panorámico nacional
          onLoad={onMapLoad}
          onIdle={onMapIdle}
          options={mapOptions}
        >
          {stores.map((store) => (
            <Marker
              key={store._id}
              position={{ lat: store.location.coordinates[1], lng: store.location.coordinates[0] }}
              onClick={() => setSelectedStore(store)}
            />
          ))}

          {/* Modal del marcador activo as OverlayView */}
          <AnimatePresence>
            {selectedStore && (
              <OverlayView
                position={{ lat: selectedStore.location.coordinates[1], lng: selectedStore.location.coordinates[0] }}
                mapPaneName={OverlayView.FLOAT_PANE}
                getPixelPositionOffset={(w) => ({
                  x: -(w / 2),
                  y: -50 // Distancia fija respecto al centro del pin de Google
                })}
              >
                <div style={{ transform: 'translateY(-100%)' }}>
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-gray-900 border border-white/10 shadow-2xl rounded-2xl p-4 w-64 backdrop-blur-md relative"
                  >
                    {/* Cierre */}
                    <button onClick={(e) => { e.stopPropagation(); setSelectedStore(null); }} className="absolute top-3 right-3 text-gray-400 hover:text-white">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>

                    <h3 className="font-bold text-white text-lg pr-6 leading-tight mb-1">{selectedStore.name}</h3>
                    <span className="text-[10px] uppercase font-bold text-yellow-500 mb-3 block">{selectedStore.category?.name}</span>
                    
                    {selectedStore.address && (
                      <div className="flex items-start text-xs text-gray-300 mb-2">
                        <MapPin size={12} className="mr-1.5 mt-0.5 text-gray-500 flex-shrink-0" />
                        <span>{selectedStore.address}</span>
                      </div>
                    )}

                    <a 
                      href={`https://www.google.com/maps/dir/?api=1&destination=${selectedStore.location.coordinates[1]},${selectedStore.location.coordinates[0]}`}
                      target="_blank" rel="noopener noreferrer"
                      className="mt-4 flex items-center justify-center w-full bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold py-2 rounded-lg transition-colors"
                    >
                      Cómo llegar
                    </a>
                    
                  </motion.div>
                </div>
              </OverlayView>
            )}
          </AnimatePresence>
        </GoogleMap>
      ) : null}
      
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.02);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default StoresLocatorPage;
